/**
 * Uploads auth letter documents (queue-first with direct API fallback).
 * @module uploadAuthLetter
 */

import { FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE } from "../../constants/stringLiterals";
import { RPATaskRequest, RPATaskResponse } from "../../data-model/rpaTaskQueue";
import { API_ENDPOINTS, RPA_PATHS_ONLY_FOR_QUEUE } from "../../enums/apiUrls";
import { PortalIdForRabbitMq } from "../../enums/portalIdsForRabbitMq";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";
import { sendRpaRequestsToRabbitMq } from "./sendRpaRequestsToRabbitMq";

/** One document reference included in an auth-letter upload request. */
export interface UploadDocument {
  document_type?: string;
  document_name: string;
  document_path: string;
}

/** Request body for uploading one or more auth letter files for an order. */
export interface UploadAuthLetterPayload {
  order_id: string;
  patient_id: string;
  documents: UploadDocument[];
  org_id: string;
  /** Linked sibling order ids to include in the same upload request. */
  sibling_order_ids?: string[];
  portal_id?: string;
  emr_name?: string;
}

/**
 * Sends a direct API request to upload auth letter.
 */
const sendDirectApiRequest = async (
  payload: UploadAuthLetterPayload,
  token: string,
): Promise<RPATaskResponse> => {
  const response = await fetch(API_ENDPOINTS.UPLOAD_AUTH_LETTER, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (response.status >= 400) {
    throw new Error("Failed to upload auth letter");
  }

  await response.json();
  return {
    success: true,
    message: "Auth letter uploaded successfully",
  };
};

/**
 * Builds the RPA task request payload for the queue.
 */
const buildRpaTaskRequest = (
  payload: UploadAuthLetterPayload,
  token: string,
): RPATaskRequest => ({
  payload,
  org_id: payload.org_id,
  portal_id: PortalIdForRabbitMq.ONCOEMR,
  priority: FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE,
  identifier: payload?.order_id ?? "",
  order_id: payload?.order_id ?? "",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Uploads auth letter via RPA queue with direct API fallback.
 * First attempts to queue the request via RabbitMQ. If that fails,
 * falls back to a direct API call.
 */
export const uploadAuthLetter = async (
  payload: UploadAuthLetterPayload,
): Promise<RPATaskResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("No token found");
    }

    const rpaRequest = buildRpaTaskRequest(payload, token);
    const queueResponse = await sendRpaRequestsToRabbitMq(
      RPA_PATHS_ONLY_FOR_QUEUE.UPLOAD_AUTH_LETTER,
      rpaRequest,
    );

    if (queueResponse.success) {
      return queueResponse;
    }

    // Fallback to direct API call if queue fails
    return await sendDirectApiRequest(payload, token);
  } catch (error) {
    logError(error as Error, "Error in uploadAuthLetter");
    throw error;
  }
};
